/**
 * Chat Service
 * Handles all chat-related API calls
 */

import apiClient from '../client';
import { API_ENDPOINTS } from '../config';
import {
  ChatThread,
  ChatMessage,
  ChatThreadsParams,
  SendMessageRequest,
  PaginatedResponse,
  VersionStats,
} from '../types';

class ChatService {
  /**
   * Get all chat threads
   */
  async getChatThreads(params?: ChatThreadsParams): Promise<PaginatedResponse<ChatThread>> {
    const response = await apiClient.get<PaginatedResponse<ChatThread>>(
      API_ENDPOINTS.CHATS.THREADS,
      { params }
    );
    return response.data;
  }

  /**
   * Get chat thread by ID
   */
  async getChatThreadById(id: string): Promise<ChatThread> {
    const response = await apiClient.get<ChatThread>(
      API_ENDPOINTS.CHATS.BY_ID(id)
    );
    return response.data;
  }

  /**
   * Get messages for a chat thread
   */
  async getMessages(
    threadId: string,
    params?: {
      page?: number;
      limit?: number;
    }
  ): Promise<PaginatedResponse<ChatMessage>> {
    const response = await apiClient.get<PaginatedResponse<ChatMessage>>(
      API_ENDPOINTS.CHATS.MESSAGES(threadId),
      { params }
    );
    return response.data;
  }

  /**
   * Send message to chat thread
   */
  async sendMessage(data: SendMessageRequest): Promise<ChatMessage> {
    const response = await apiClient.post<ChatMessage>(
      API_ENDPOINTS.CHATS.MESSAGES(data.threadId),
      {
        content: data.content,
        type: data.type,
      }
    );
    return response.data;
  }

  /**
   * Create new chat thread
   */
  async createChatThread(data: {
    userId: string;
    title: string;
    initialMessage?: string;
  }): Promise<ChatThread> {
    const response = await apiClient.post<ChatThread>(
      API_ENDPOINTS.CHATS.THREADS,
      data
    );
    return response.data;
  }

  /**
   * Update chat thread
   */
  async updateChatThread(
    id: string,
    data: {
      title?: string;
    }
  ): Promise<ChatThread> {
    const response = await apiClient.put<ChatThread>(
      API_ENDPOINTS.CHATS.BY_ID(id),
      data
    );
    return response.data;
  }

  /**
   * Delete chat thread
   */
  async deleteChatThread(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.CHATS.BY_ID(id));
  }

  /**
   * Get chat statistics
   */
  async getChatStats(params?: {
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
  }): Promise<{
    totalThreads: number;
    totalMessages: number;
    activeThreads: number;
    averageMessagesPerThread: number;
  }> {
    const response = await apiClient.get<{
      totalThreads: number;
      totalMessages: number;
      activeThreads: number;
      averageMessagesPerThread: number;
    }>(API_ENDPOINTS.CHATS.STATS, { params });
    return response.data;
  }

  /**
   * Get opened chats statistics
   */
  async getOpenedChatsStats(): Promise<{
    stats: VersionStats[];
    totals: {
      last24h: number;
      lastWeek: number;
      total: number;
    };
  }> {
    const response = await apiClient.get<{
      stats: VersionStats[];
      totals: {
        last24h: number;
        lastWeek: number;
        total: number;
      };
    }>(API_ENDPOINTS.CHATS.OPENED);
    return response.data;
  }

  /**
   * Get chat trends
   */
  async getChatTrends(params?: {
    period?: 'day' | 'week' | 'month';
  }): Promise<Array<{
    date: string;
    threadCount: number;
    messageCount: number;
    uniqueUsers: number;
  }>> {
    const response = await apiClient.get<Array<{
      date: string;
      threadCount: number;
      messageCount: number;
      uniqueUsers: number;
    }>>(`${API_ENDPOINTS.CHATS.STATS}/trends`, { params });
    return response.data;
  }

  /**
   * Get most active users in chats
   */
  async getMostActiveUsers(params?: {
    limit?: number;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Array<{
    userId: string;
    username: string;
    threadCount: number;
    messageCount: number;
  }>> {
    const response = await apiClient.get<Array<{
      userId: string;
      username: string;
      threadCount: number;
      messageCount: number;
    }>>(`${API_ENDPOINTS.CHATS.STATS}/active-users`, { params });
    return response.data;
  }

  /**
   * Search chat messages
   */
  async searchMessages(
    query: string,
    params?: {
      threadId?: string;
      userId?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<PaginatedResponse<ChatMessage>> {
    const response = await apiClient.get<PaginatedResponse<ChatMessage>>(
      `${API_ENDPOINTS.CHATS.BASE}/search`,
      {
        params: {
          ...params,
          query,
        },
      }
    );
    return response.data;
  }

  /**
   * Export chat threads
   */
  async exportChatThreads(params?: ChatThreadsParams): Promise<Blob> {
    const response = await apiClient.getAxiosInstance().get(
      `${API_ENDPOINTS.CHATS.THREADS}/export`,
      {
        params,
        responseType: 'blob',
      }
    );
    return response.data;
  }

  /**
   * Export chat messages for a thread
   */
  async exportChatMessages(threadId: string): Promise<Blob> {
    const response = await apiClient.getAxiosInstance().get(
      `${API_ENDPOINTS.CHATS.MESSAGES(threadId)}/export`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }
}

// Export singleton instance
export const chatService = new ChatService();
export default chatService;

